const IDENTITY_KEYS_WHEN_DISPLAY = new Set(['id', 'uniqueKey']);
const SELECTED_CHOICE_KEYS = new Set([
  'selectedChoice',
  'selectedChoices',
  'selectedChoiceId',
  'selectedChoiceIds',
]);

export type AnswerDetailSection = {
  title: string;
  subtitle?: string;
};

export function isAddressLikeAnswer(
  value: unknown,
): value is Record<string, unknown> {
  if (!isPlainObject(value)) {
    return false;
  }
  if (
    !('latitude' in value || 'longitude' in value || 'placeId' in value)
  ) {
    return false;
  }
  return (
    readTrimmedString(value, 'display').length > 0 ||
    readTrimmedString(value, 'address').length > 0
  );
}

export function readAddressLikeAnswer(value: unknown): {
  display: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  placeId: string | null;
} | null {
  if (!isAddressLikeAnswer(value)) {
    return null;
  }
  const latitude = readFiniteNumber(value, 'latitude');
  const longitude = readFiniteNumber(value, 'longitude');
  const placeId = readTrimmedString(value, 'placeId') || null;
  const display = readTrimmedString(value, 'display');
  const address = readTrimmedString(value, 'address');
  return {
    display: display || address,
    address: address || display,
    latitude,
    longitude,
    placeId,
  };
}

export function formatAnswerValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    const n = value.length;
    return n === 1 ? '1 answer' : `${n} answers`;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (isPlainObject(value)) {
    if (isAddressLikeAnswer(value)) {
      return (
        readTrimmedString(value, 'display') ||
        readTrimmedString(value, 'address')
      );
    }
    const display = readTrimmedString(value, 'display');
    if (display) {
      const secondary = readTrimmedString(value, 'secondaryDisplay');
      return secondary ? `${display} (${secondary})` : display;
    }
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function answerObjectHasId(value: unknown): value is Record<string, unknown> {
  if (!isPlainObject(value)) {
    return false;
  }
  const id = value['id'];
  if (typeof id === 'string') {
    return id.trim().length > 0;
  }
  if (typeof id === 'number') {
    return Number.isFinite(id);
  }
  return false;
}

export function isAnswerHoverable(value: unknown): boolean {
  if (isAddressLikeAnswer(value)) {
    return true;
  }
  if (answerObjectHasId(value)) {
    return true;
  }
  if (!Array.isArray(value) || value.length === 0) {
    return false;
  }
  return value.some(isArrayDetailItem);
}

/**
 * Collection-backed select / searchable_picker snapshot:
 * `{ id, display, uniqueKey? }` without grid choice keys.
 */
export function isCollectionEntityAnswer(
  value: unknown,
): value is Record<string, unknown> {
  if (!answerObjectHasId(value)) {
    return false;
  }
  for (const key of SELECTED_CHOICE_KEYS) {
    if (key in value) {
      return false;
    }
  }
  const display = value['display'];
  return typeof display === 'string' && display.trim().length > 0;
}

export function readEntityAnswerId(value: unknown): string | null {
  if (!isCollectionEntityAnswer(value)) {
    return null;
  }
  const id = value['id'];
  if (typeof id === 'string' && id.trim()) {
    return id.trim();
  }
  if (typeof id === 'number' && Number.isFinite(id)) {
    return String(id);
  }
  return null;
}

export function buildAnswerDetailSections(value: unknown): AnswerDetailSection[] {
  if (Array.isArray(value)) {
    return buildAnswerArraySections(value);
  }
  return buildAnswerObjectSections(value);
}

export function formatAnswerDetails(value: unknown): string {
  return buildAnswerDetailSections(value)
    .map((section) =>
      section.subtitle ? `${section.title}\n${section.subtitle}` : section.title,
    )
    .join('\n\n');
}

export function formatAnswerObjectDetails(value: unknown): string {
  return buildAnswerObjectSections(value)
    .map((section) =>
      section.subtitle ? `${section.title}\n${section.subtitle}` : section.title,
    )
    .join('\n');
}

function buildAnswerArraySections(items: unknown[]): AnswerDetailSection[] {
  const sections: AnswerDetailSection[] = [];

  for (const item of items) {
    if (item === null || item === undefined) {
      continue;
    }

    if (typeof item === 'string') {
      const trimmed = item.trim();
      if (trimmed) {
        sections.push({ title: trimmed });
      }
      continue;
    }

    if (!isPlainObject(item)) {
      continue;
    }

    const display = readTrimmedString(item, 'display');
    const secondary = readTrimmedString(item, 'secondaryDisplay');
    const selectedLabels = readSelectedChoiceLabels(item);
    const selectedLine =
      selectedLabels.length > 0 ? formatSelectedLine(selectedLabels) : '';
    const numberGridLine = formatNumberGridValuesLine(item);

    if (display) {
      sections.push({
        title: secondary ? `${display} (${secondary})` : display,
        subtitle: selectedLine || numberGridLine || undefined,
      });
      continue;
    }

    if (selectedLine) {
      sections.push({ title: selectedLine });
      continue;
    }

    if (numberGridLine) {
      sections.push({ title: numberGridLine });
      continue;
    }

    const fallback = formatAnswerValue(item);
    if (fallback) {
      sections.push({ title: fallback });
    }
  }

  return sections;
}

function formatNumberGridValuesLine(row: Record<string, unknown>): string {
  const values = row['values'];
  if (!Array.isArray(values) || values.length === 0) {
    return '';
  }
  const parts: string[] = [];
  for (const entry of values) {
    if (!isPlainObject(entry)) {
      continue;
    }
    const label =
      readTrimmedString(entry, 'label') ||
      readTrimmedString(entry, 'id') ||
      readTrimmedString(entry, 'key');
    if (!label) {
      continue;
    }
    const raw = entry['value'];
    const valueText =
      raw === null || raw === undefined || raw === ''
        ? '—'
        : typeof raw === 'number' && Number.isFinite(raw)
          ? String(raw)
          : String(raw);
    parts.push(`${label}: ${valueText}`);
  }
  return parts.join(', ');
}

function buildAnswerObjectSections(value: unknown): AnswerDetailSection[] {
  if (!answerObjectHasId(value)) {
    return [];
  }

  const sections: AnswerDetailSection[] = [];
  const display = readTrimmedString(value, 'display');
  const secondary = readTrimmedString(value, 'secondaryDisplay');
  const selectedLabels = readSelectedChoiceLabels(value);
  const selectedLine =
    selectedLabels.length > 0 ? formatSelectedLine(selectedLabels) : '';

  const numberGridLine = formatNumberGridValuesLine(value);

  if (display) {
    sections.push({
      title: secondary ? `${display} (${secondary})` : display,
      subtitle: selectedLine || numberGridLine || undefined,
    });
  } else if (selectedLine) {
    sections.push({ title: selectedLine });
  } else if (numberGridLine) {
    sections.push({ title: numberGridLine });
  }

  const skipKeys = new Set<string>([
    'display',
    'secondaryDisplay',
    'values',
    ...SELECTED_CHOICE_KEYS,
  ]);
  if (display) {
    for (const key of IDENTITY_KEYS_WHEN_DISPLAY) {
      skipKeys.add(key);
    }
  }

  for (const key of Object.keys(value).sort((a, b) => a.localeCompare(b))) {
    if (skipKeys.has(key)) {
      continue;
    }
    const raw = value[key];
    if (raw === null || raw === undefined) {
      continue;
    }
    if (typeof raw === 'string' && !raw.trim()) {
      continue;
    }
    sections.push({
      title: key,
      subtitle: formatDetailEntry(raw),
    });
  }

  return sections;
}

function formatSelectedLine(labels: string[]): string {
  return `Selected: ${labels.join(', ')}`;
}

function isArrayDetailItem(item: unknown): boolean {
  if (typeof item === 'string') {
    return item.trim().length > 0;
  }
  if (!isPlainObject(item)) {
    return false;
  }
  const values = item['values'];
  if (Array.isArray(values) && values.length > 0) {
    return true;
  }
  if (readTrimmedString(item, 'display')) {
    return true;
  }
  if (answerObjectHasId(item)) {
    return true;
  }
  return readSelectedChoiceLabels(item).length > 0;
}

/**
 * Prefer snapshotted labels; never use id when a non-empty label exists.
 * Legacy string-id-only rows still fall back to the id string.
 */
function readSelectedChoiceLabels(value: Record<string, unknown>): string[] {
  const snapshots = value['selectedChoices'];
  if (Array.isArray(snapshots) && snapshots.length > 0) {
    const labels = snapshots
      .map((item) => choiceDisplayLabel(item))
      .filter(Boolean);
    if (labels.length > 0) {
      return labels;
    }
  }

  const singleSnapshot = value['selectedChoice'];
  if (singleSnapshot !== null && singleSnapshot !== undefined) {
    const singleLabel = choiceDisplayLabel(singleSnapshot);
    if (singleLabel) {
      return [singleLabel];
    }
  }

  const legacyIds = value['selectedChoiceIds'];
  if (Array.isArray(legacyIds)) {
    const ids = legacyIds
      .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
      .map((id) => id.trim());
    if (ids.length > 0) {
      return ids;
    }
  }

  const legacyId = readTrimmedString(value, 'selectedChoiceId');
  return legacyId ? [legacyId] : [];
}

function choiceDisplayLabel(raw: unknown): string {
  if (typeof raw === 'string') {
    return raw.trim();
  }
  if (!isPlainObject(raw)) {
    return '';
  }
  const label = readTrimmedString(raw, 'label');
  if (label) {
    return label;
  }
  return readTrimmedString(raw, 'id');
}

function formatDetailEntry(raw: unknown): string {
  if (typeof raw === 'string') {
    return raw.trim();
  }
  if (typeof raw === 'number' || typeof raw === 'boolean') {
    return String(raw);
  }
  if (Array.isArray(raw)) {
    const labels = raw.map((item) => choiceDisplayLabel(item)).filter(Boolean);
    if (labels.length === raw.length && labels.length > 0) {
      return labels.join(', ');
    }
    try {
      return JSON.stringify(raw);
    } catch {
      return String(raw);
    }
  }
  if (isPlainObject(raw)) {
    const label = choiceDisplayLabel(raw);
    if (label && ('label' in raw || 'id' in raw)) {
      const hasLabel = readTrimmedString(raw, 'label');
      if (hasLabel) {
        return hasLabel;
      }
    }
    const display = readTrimmedString(raw, 'display');
    if (display) {
      return display;
    }
  }
  try {
    return JSON.stringify(raw);
  } catch {
    return String(raw);
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readTrimmedString(
  value: Record<string, unknown>,
  key: string,
): string {
  const raw = value[key];
  return typeof raw === 'string' ? raw.trim() : '';
}

function readFiniteNumber(
  value: Record<string, unknown>,
  key: string,
): number | null {
  const raw = value[key];
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}
